import React, { ReactElement } from 'react';
import { pdf } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';

import { CategoryQuantity, RelevoInterface, RelevoInventoryResponse } from '../../../../api/types';
import { Language } from '../../../../contexts/LocalizationContext/types';
import { AddressResponse } from '../../../Restaurants/types';
import { DeliveryNotePdfDocument } from './DeliveryNotePdfDocument';

export type DeliveryNoteDetail = {
  name: string;
  uid?: string | null;
  category?: string | null;
};

export type DeliveryNoteSummary = {
  category: string;
  quantity: number;
  items?: DeliveryNoteDetail[];
};

export type DeliveryNoteContext = {
  summaryItems: DeliveryNoteSummary[];
  orderNumber?: string | null;
  locationName?: string | null;
  locationAddress?: AddressResponse | null;
  quickContactName: string;
  quickContactAddress?: AddressResponse | null;
};

type GenerateDeliveryNotePdfParams = {
  generatedAt?: Date;
  context: DeliveryNoteContext;
  filePrefix?: string;
  translations: (key: string) => string;
};

export const generateDeliveryNotePdf = async ({ context, translations }: GenerateDeliveryNotePdfParams) => {
  if (!context.summaryItems.length) {
    return;
  }

  const effectiveGeneratedAt = new Date();
  const fallbackOrderNumber = `${format(effectiveGeneratedAt, 'yyyyMMdd-HHmm')}`;
  const safeOrderNumber =
    context.orderNumber && context.orderNumber.trim().length ? context.orderNumber : fallbackOrderNumber;

  const documentElement = React.createElement(DeliveryNotePdfDocument, {
    orderNumber: context.orderNumber,
    locationName: context.locationName ?? null,
    locationAddress: context.locationAddress ?? null,
    quickContactName: context.quickContactName,
    quickContactAddress: context.quickContactAddress ?? null,
    totalQuantity: context.summaryItems.reduce((sum, item) => sum + item.quantity, 0),
    summaryItems: context.summaryItems,
    labels: {
      deliveryNoteTitle: translations('delivery_note_title'),
      orderNo: translations('delivery_note_order_no'),
      scanDate: translations('delivery_note_scan_date'),
      summaryOfScannedItems: translations('delivery_note_summary_title'),
      position: translations('delivery_note_position'),
      category: translations('delivery_note_category'),
      quantity: translations('delivery_note_quantity'),
      itemNames: translations('delivery_note_item_names'),
      itemsSection: translations('delivery_note_items_section'),
      totalNumberOfItemsPrefix: translations('delivery_note_total_prefix'),
      itemsReceivedNote: translations('delivery_note_items_received_note'),
      signatureDeliverer: translations('delivery_note_signature_deliverer'),
      signatureReceiver: translations('delivery_note_signature_receiver'),
      signatureDate: translations('delivery_note_signature_date'),
    },
  }) as unknown as ReactElement;

  const blob = await pdf(documentElement).toBlob();
  const fileName = `delivery-note-${safeOrderNumber}.pdf`;

  saveAs(blob, fileName);
};

type BuildKnownDeliveryNoteContextParams = {
  assignedItems: string[];
  scannedRelevos: RelevoInterface[];
  quickContactName: string;
  quickContactAddress: AddressResponse | null;
  restaurantName: string | null;
  restaurantAddress: AddressResponse | null;
  orderNumber?: string | null;
  language: Language;
  unknownItemLabel: string;
};

export const buildKnownDeliveryNoteContext = ({
  assignedItems,
  scannedRelevos,
  quickContactName,
  quickContactAddress,
  restaurantName,
  restaurantAddress,
  orderNumber,
  language,
  unknownItemLabel,
}: BuildKnownDeliveryNoteContextParams): DeliveryNoteContext | null => {
  const assignedDetails = scannedRelevos.filter((relevo) => assignedItems.includes(relevo.uId));

  if (!assignedDetails.length) {
    return null;
  }

  const summaryItemsMap = new Map<string, DeliveryNoteSummary>();

  assignedDetails.forEach((relevo) => {
    const categoryName = relevo.category.translations[language] || 'Unknown category';

    if (!summaryItemsMap.has(categoryName)) {
      summaryItemsMap.set(categoryName, { category: categoryName, quantity: 0, items: [] });
    }

    const summaryEntry = summaryItemsMap.get(categoryName)!;
    summaryEntry.quantity += 1;
    summaryEntry.items = summaryEntry.items || [];
    summaryEntry.items.push({
      name: relevo.productName || unknownItemLabel,
      uid: relevo.uId,
      category: categoryName,
    });
  });

  return {
    summaryItems: Array.from(summaryItemsMap.values()),
    orderNumber,
    locationName: restaurantName,
    locationAddress: restaurantAddress,
    quickContactName,
    quickContactAddress: quickContactAddress ?? undefined,
  };
};

type BuildUnknownDeliveryNoteContextParams = {
  assignedCategories: CategoryQuantity;
  inventory?: RelevoInventoryResponse;
  quickContactName: string;
  quickContactAddress: AddressResponse | null;
  restaurantName: string | null;
  restaurantAddress: AddressResponse | null;
  orderNumber?: string | null;
  issuedAt?: Date | null;
  language: Language;
};

export const buildUnknownDeliveryNoteContext = ({
  assignedCategories,
  inventory,
  quickContactName,
  quickContactAddress,
  restaurantName,
  restaurantAddress,
  orderNumber,
  language,
}: BuildUnknownDeliveryNoteContextParams): DeliveryNoteContext | null => {
  const summaryItems: DeliveryNoteSummary[] = Object.entries(assignedCategories)
    .filter(([, quantity]) => quantity > 0)
    .map(([categoryId, quantity]) => ({
      category:
        inventory?.categoriesSummary.find((category) => category.id === categoryId)?.translations?.[language] ||
        categoryId,
      quantity,
    }));

  if (!summaryItems.length) {
    return null;
  }

  return {
    summaryItems,
    orderNumber,
    locationName: restaurantName,
    locationAddress: restaurantAddress,
    quickContactName,
    quickContactAddress: quickContactAddress ?? undefined,
  };
};
